// Role-based access control utility
export const USER_ROLES = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  ADMIN: 'ADMIN',
  PHARMACY_ADMIN: 'PHARMACY_ADMIN',
  PHARMACY_USER: 'PHARMACY_USER'
}

export const ROLE_HIERARCHY = {
  [USER_ROLES.SUPER_ADMIN]: 4,
  [USER_ROLES.ADMIN]: 3,
  [USER_ROLES.PHARMACY_ADMIN]: 2,
  [USER_ROLES.PHARMACY_USER]: 1
}

/**
 * Check if user has a specific role
 */
export function hasRole(user, role) {
  return user?.role === role
}

/**
 * Check if user has any of the specified roles
 */
export function hasAnyRole(user, roles) {
  return user && roles.includes(user.role)
}

/**
 * Check if user has at least the specified role level
 */
export function hasRoleLevel(user, minRole) {
  if (!user?.role) return false
  return ROLE_HIERARCHY[user.role] >= ROLE_HIERARCHY[minRole]
}

/**
 * Check if user is an admin (SUPER_ADMIN or ADMIN)
 */
export function isAdmin(user) {
  return hasAnyRole(user, [USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN])
}

/**
 * Check if user is a super admin
 */
export function isSuperAdmin(user) {
  return hasRole(user, USER_ROLES.SUPER_ADMIN)
}

/**
 * Check if user is a pharmacy user (PHARMACY_ADMIN or PHARMACY_USER)
 */
export function isPharmacyUser(user) {
  return hasAnyRole(user, [USER_ROLES.PHARMACY_ADMIN, USER_ROLES.PHARMACY_USER])
}

/**
 * Check if user is a pharmacy admin
 */
export function isPharmacyAdmin(user) {
  return hasRole(user, USER_ROLES.PHARMACY_ADMIN)
}

/**
 * Check if user has a pharmacy assigned
 */
export function hasPharmacyAssigned(user) {
  return !!(user?.pharmacy || user?.pharmacy_id)
}

/**
 * Get dashboard type based on user role and pharmacy assignment
 */
export function getDashboardType(user) {
  if (!user) return 'default'
  
  // If user has a pharmacy assigned, show pharmacy dashboard
  if (hasPharmacyAssigned(user)) {
    return 'pharmacy'
  }
  
  // Otherwise, show admin dashboard for admin users
  if (isAdmin(user)) {
    return 'admin'
  }
  
  return 'default'
}

/**
 * Check if user can access admin dashboard
 */
export function canAccessAdminDashboard(user) {
  return isAdmin(user)
}

/**
 * Check if user can access pharmacy dashboard
 */
export function canAccessPharmacyDashboard(user) {
  return isPharmacyUser(user)
}

/**
 * Check if user can manage users
 */
export function canManageUsers(user) {
  if (!user) return false
  
  // Super admins and admins can manage users only when NO pharmacy is assigned
  if (isAdmin(user)) {
    return !hasPharmacyAssigned(user)
  }
  
  return false
}

/**
 * Check if user can manage pharmacy users (for pharmacy admins)
 */
export function canManagePharmacyUsers(user) {
  if (!user) return false
  
  // Super admins, admins, and pharmacy admins can manage pharmacy users
  // For admins, they need to have a pharmacy assigned
  if (isAdmin(user)) {
    return hasPharmacyAssigned(user)
  }
  
  return isSuperAdmin(user) || isPharmacyAdmin(user)
}

/**
 * Check if user can manage pharmacies
 */
export function canManagePharmacies(user) {
  if (!user) return false
  
  // Super admins and admins can manage pharmacies only when NO pharmacy is assigned
  if (isAdmin(user)) {
    return !hasPharmacyAssigned(user)
  }
  
  return false
}

/**
 * Check if user can edit their own pharmacy
 */
export function canEditOwnPharmacy(user) {
  return isPharmacyAdmin(user)
}

/**
 * Check if user can view pharmacy list
 */
export function canViewPharmacyList(user) {
  if (!user) return false
  
  // Super admins and admins can view pharmacy list only when NO pharmacy is assigned
  if (isAdmin(user)) {
    return !hasPharmacyAssigned(user)
  }
  
  return false
}

/**
 * Check if user can edit pharmacy list (create/edit/delete)
 */
export function canEditPharmacyList(user) {
  if (!user) return false
  
  // Super admins and admins can edit pharmacy list only when NO pharmacy is assigned
  if (isAdmin(user)) {
    return !hasPharmacyAssigned(user)
  }
  
  return false
}

/**
 * Check if user can manage master products (create/edit/delete products in the catalog)
 */
export function canManageMasterProducts(user) {
  // Only super admins and admins (WITHOUT assigned pharmacy) can manage master products
  if (!user) return false
  return isAdmin(user) && !hasPharmacyAssigned(user)
}

/**
 * Check if user can manage pharmacy products (customize products for their pharmacy)
 */
export function canManagePharmacyProducts(user) {
  // Pharmacy admins and users can manage pharmacy products
  // Admins with pharmacy assigned can also manage pharmacy products
  if (!user) return false
  return isPharmacyUser(user) || (isAdmin(user) && hasPharmacyAssigned(user))
}

/**
 * Check if user can view master product catalog
 */
export function canViewMasterProducts(user) {
  // All authenticated users can view the master catalog
  return !!user
}

/**
 * Check if user can access products section
 */
export function canAccessProducts(user) {
  if (!user) return false
  
  // Super admins and admins can access products (either master or pharmacy)
  if (isAdmin(user)) {
    return true
  }
  
  // Pharmacy admins and users can access pharmacy products
  if (isPharmacyUser(user)) {
    return true
  }
  
  return false
}

/**
 * Check if user can access requests
 */
export function canAccessRequests(user) {
  if (!user) return false
  
  // Super admins and admins can access requests only when pharmacy is assigned
  if (isAdmin(user)) {
    return hasPharmacyAssigned(user)
  }
  
  // Pharmacy admins and users can access requests (they always have a pharmacy)
  if (isPharmacyUser(user)) {
    return true
  }
  
  return false
}

/**
 * Check if user can access prescriptions
 */
export function canAccessPrescriptions(user) {
  if (!user) return false
  
  // Super admins and admins can access prescriptions only when pharmacy is assigned
  if (isAdmin(user)) {
    return hasPharmacyAssigned(user)
  }
  
  // Pharmacy admins and users can access prescriptions (they always have a pharmacy)
  if (isPharmacyUser(user)) {
    return true
  }
  
  return false
}

/**
 * Check if user can access developers section
 */
export function canAccessDevelopers(user) {
  if (!user) return false
  
  // Super admins and admins can access developers section only when NO pharmacy is assigned
  if (isAdmin(user)) {
    return !hasPharmacyAssigned(user)
  }
  
  return false
}

/**
 * Get available roles for user creation based on current user's role
 */
export function getAvailableRolesForCreation(currentUser) {
  if (!currentUser) return [];

  if (isSuperAdmin(currentUser) || isAdmin(currentUser)) {
    // Super admins and admins can create other admins and pharmacy users
    return [USER_ROLES.ADMIN, USER_ROLES.PHARMACY_ADMIN, USER_ROLES.PHARMACY_USER];
  } else if (isPharmacyAdmin(currentUser)) {
    // Pharmacy admins can only create pharmacy users
    return [USER_ROLES.PHARMACY_ADMIN, USER_ROLES.PHARMACY_USER];
  }

  return [];
}

/**
 * Get user's accessible menu items based on role
 */
export function getAccessibleMenuItems(user) {
  if (!user) return {}

  const hasPharmacy = hasPharmacyAssigned(user)
  const isAdminUser = isAdmin(user)

  const menuItems = {
    dashboard: true, // Always show dashboard
    users: isAdminUser && !hasPharmacy, // Only show users menu for admins without pharmacy
    pharmacyUsers: hasPharmacy, // Show pharmacy users when pharmacy assigned
    pharmacies: isAdminUser && !hasPharmacy, // Only show pharmacies menu for admins without pharmacy
    products: canAccessProducts(user), // Show products based on access control
    requests: hasPharmacy, // Show requests when pharmacy assigned
    prescriptions: hasPharmacy, // Show prescriptions when pharmacy assigned
    developers: hasPharmacy // Only show developers when pharmacy is assigned
  }

  console.log('Menu visibility:', {
    user: user?.id,
    role: user?.role,
    hasPharmacy,
    isAdminUser,
    productsVisible: menuItems.products
  })

  return menuItems
}

/**
 * Get user's accessible submenu items
 */
export function getAccessibleSubmenuItems(user) {
  if (!user) return {}

  const hasPharmacy = hasPharmacyAssigned(user)
  const isAdminUser = isAdmin(user)

  return {
    users: {
      list: isAdminUser && !hasPharmacy,
      create: isAdminUser && !hasPharmacy
    },
    pharmacyUsers: {
      list: hasPharmacy,
      create: hasPharmacy
    },
    pharmacies: {
      list: isAdminUser && !hasPharmacy,
      create: isAdminUser && !hasPharmacy
    },
    requests: {
      list: hasPharmacy,
      create: hasPharmacy
    },
    products: {
      // For pharmacy users: show pharmacy product management
      // For admin users: show master product management
      list: hasPharmacy ? canManagePharmacyProducts(user) : canViewMasterProducts(user),
      create: hasPharmacy ? canManagePharmacyProducts(user) : canManageMasterProducts(user),
      upload: hasPharmacy ? canManagePharmacyProducts(user) : canManageMasterProducts(user),
      unlinkedReview: hasPharmacy ? canManagePharmacyProducts(user) : false, // Only show in pharmacy mode
      // Legacy pharmacy-specific keys (for backward compatibility)
      pharmacyList: canManagePharmacyProducts(user),
      pharmacyCreate: canManagePharmacyProducts(user),
      pharmacyMapping: canManagePharmacyProducts(user)
    },
    prescriptions: {
      list: hasPharmacy,
      create: hasPharmacy
    },
    developers: {
      apiTokens: hasPharmacy,
      apiDocumentation: hasPharmacy,
      graphqlPlayground: hasPharmacy
    }
  }
} 